import {
  Autocomplete,
  Box,
  Button,
  Divider,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React from "react";
import Modal, { CommonModalProps } from "..";
import CopyPermissionPopperRole from "../../Misc/CopyPermissionPopper/copy-permission-role-popper";
import CopyPermissionPopperGroup from "../../Misc/CopyPermissionPopper/copy-permission-group-popper";
import PermissionAccordionList from "../../Lists/PermissionsAccordionList/permission-accordion-list";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { DbUserGroupInfos } from "../../../Core/Api/Types/db-user-group-infos";
import { produce } from "immer";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import { ingestService, orderService, shareService, userService } from "../../../Core/Api/services";
import { CreateGroupRequest } from "../../../Core/Api/Types/create-group-request";
import { DbLabProviderInfos } from "../../../Core/Api/Types/db-lab-provider-infos";
import { AclAssetAccessLevel } from "../../../Core/Api/Types/acl-asset-access-level";
import { AclWorkflowAccessLevel } from "../../../Core/Api/Types/acl-workflow-access-level";
import { CurrencyTypes } from "../../../Core/Api/Types/currency-types";
import { DbUserGroupEntityInfos } from "../../../Core/Api/Types/db-user-group-entity-infos";
import { ObjectClass } from "../../../Core/Api/Types/object-class";
import { ObjectLayer } from "../../../Core/Api/Types/object-layer";
import FormSection from "../../Forms/FormSection/form-section";
import { DbUserGroupRoleInfos } from "../../../Core/Api/Types/db-user-group-role-infos";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { useTranslation } from "react-i18next";
import { minutesToTimeSpan, timeSpanToMinutes } from "../../../Utils/helpers";

type CloseClbProps = {
  confirm: boolean;
};

export type ModalAddGroupElementProps = {
  onClose?: (props: CloseClbProps) => void;
  onConfirmModalCreateGroup?: (group: DbUserGroupInfos) => void;
};
export type ModalAddGroupProps = CommonModalProps<ModalAddGroupElementProps>;

const ModalAddGroup = React.forwardRef<any, ModalAddGroupProps>((props, ref) => {
  const { closeModal, onClose, onConfirmModalCreateGroup, ...modalProps } = props;
  const { t } = useTranslation();

  const [filterValue, setFilterValue] = React.useState<string>("");
  const [permissionAnchorRole, setPermissionAnchorRole] = React.useState<HTMLButtonElement>();
  const [permissionAnchorGroup, setPermissionAnchorGroup] = React.useState<HTMLButtonElement>();
  const [newGroup, setNewGroup] = React.useState<DbUserGroupInfos>(new DbUserGroupInfos({ enabled: true }));
  const [labProvidersList, setLabProvidersList] = React.useState<DbLabProviderInfos[]>([]);
  const [currencies, setCurrencies] = React.useState<CurrencyTypes[] | null>();
  const [entities, setEntities] = React.useState<DbUserGroupEntityInfos[] | null>([]);
  const [groupRoles, setGroupRoles] = React.useState<DbUserGroupRoleInfos[]>([]);
  const enqueueCustomSnackbar = useCustomSnackbar();

  const onClickCopyPermissionsRole: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    setPermissionAnchorRole((prevState) => (prevState ? undefined : e.currentTarget));
  };

  const onClickCopyPermissionsGroup: React.MouseEventHandler<HTMLButtonElement> = (e) => {
    setPermissionAnchorGroup((prevState) => (prevState ? undefined : e.currentTarget));
  };

  const handleCancelClick = () => {
    onClose && onClose({ confirm: false });
    closeModal();
  };

  const handleConfirmClick = () => {
    const request: CreateGroupRequest = {
      group: newGroup,
      labProviders: labProvidersList.filter((x) => newGroup.labProviderIds?.includes(x.id)),
    };

    userService.CreateGroup(request).then(function (response) {
      if (response.success) {
        enqueueCustomSnackbar(t("alert:success.newGroupCreated"), "success");

        onConfirmModalCreateGroup && onConfirmModalCreateGroup({ ...request.group!, id: response.objectId! });
        closeModal();
      } else enqueueCustomSnackbar(t("alert:error.oopsWithMessage", { message: response.message }), "warning");
    });
  };

  React.useEffect(() => {
    orderService.GetCurrencies().then(function (response) {
      if (response.success) setCurrencies(response.currencies);
    });
    ingestService.GetProviders().then(function (response) {
      if (response.success) setLabProvidersList(response.providers || []);
    });
    userService.GetEntitiesList().then(function (response) {
      if (response.success) setEntities(response.entities);
    });
    shareService.GetShareInfos().then(function (response) {
      if (response.success)
        setNewGroup(
          produce((draft) => {
            draft!.defaultShareViews = response.defaultShareViews;
            draft!.defaultShareViewDays = response.defaultShareViewDays;
            draft!.defaultShareViewIPs = response.defaultShareViewIPs;
            draft!.maxShareViewDays = response.maxShareViewDays;
            draft!.maxShareViewIPs = response.maxShareViewIPs;
            draft!.maxShareViews = response.maxShareViews;
          })
        );
    });

    userService.GetGroupRolesList().then((response) => {
      if (response.groupRoles) setGroupRoles(response.groupRoles);
    });
  }, []);

  const onClickCopyPermissionsRoleValidation = React.useCallback(
    (acls: AclTypes[] | null | undefined) => {
      if (acls)
        setNewGroup(
          produce((draft) => {
            draft.acls = acls;
          })
        );
      setPermissionAnchorRole(undefined);
    },
    [newGroup]
  );

  const onClickCopyPermissionsGroupValidation = React.useCallback(
    (acls: AclTypes[] | null | undefined) => {
      if (acls)
        setNewGroup(
          produce((draft) => {
            draft.acls = acls;
          })
        );
      setPermissionAnchorGroup(undefined);
    },
    [newGroup]
  );

  return (
    <Modal
      {...modalProps}
      title={t("modal:title.createGroup")}
      reference={ref}
      closeModal={handleCancelClick}
      width={750}
      footer={
        <Stack direction="row" justifyContent="space-between">
          <Button onClick={handleCancelClick}>{t("common:cancel")}</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfirmClick}
            disabled={!newGroup.aclAssetAccessLevel || !newGroup.aclWorkflowAccessLevel || !newGroup.name}
          >
            {t("button:createGroup")}
          </Button>
        </Stack>
      }
    >
      <Grid container spacing={3}>
        <FormSection
          status={!newGroup.name || !newGroup.name.length ? "error" : "todo"}
          statusText={!newGroup.name || !newGroup.name.length ? t("form:statusText.groupNameRequired") : ""}
          title={t("common:generalInformationsLowerCase")}
          sx={{ mb: 2, ml: 3, mt: 3 }}
        >
          <Grid container spacing={2}>
            <Grid item md={6}>
              <Stack>
                <Typography component="label" fontWeight={450} sx={{ mb: 1, mt: 0.3 }}>
                  {t("field:label.selectRole")}
                </Typography>
                <Select
                  displayEmpty
                  IconComponent={ExpandMoreRoundedIcon}
                  value={newGroup.roleId || undefined}
                  sx={{ width: "100%", mb: 1 }}
                  MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
                  onChange={(e) => {
                    setNewGroup(
                      produce((draft) => {
                        draft!.roleId = groupRoles?.find((role) => role.id === +e.target.value)?.id || null;
                      })
                    );
                  }}
                >
                  <MenuItem value={undefined}>{t("menu:item.noRole")}</MenuItem>
                  {groupRoles.map((role) => (
                    <MenuItem value={role.id} key={role.id}>
                      {role.name}
                    </MenuItem>
                  ))}
                </Select>
              </Stack>
            </Grid>
            <Grid item md={6}>
              <InputLabel required sx={{ mb: 1 }}>
                {t("field:label.groupName")}
              </InputLabel>
              <OutlinedInput
                defaultValue={newGroup?.name}
                id={"name"}
                sx={{ width: "100%", mb: 1 }}
                onChange={(e) =>
                  setNewGroup(
                    produce((draft) => {
                      draft!.name = e.target.value;
                    })
                  )
                }
              />
            </Grid>
            <Grid item md={12}>
              <InputLabel sx={{ mb: 1 }}>{t("common:description")}</InputLabel>
              <OutlinedInput
                defaultValue={newGroup.description}
                id={"description"}
                sx={{ width: "100%" }}
                multiline
                onChange={(e) =>
                  setNewGroup(
                    produce((draft) => {
                      draft!.description = e.target.value;
                    })
                  )
                }
              />
            </Grid>
          </Grid>
        </FormSection>
        <FormSection title={t("form:section.title.groupAssociations")} status="todo" sx={{ mb: 2, ml: 3 }}>
          <Grid container spacing={2}>
            {currencies && (
              <Grid item md={6}>
                <InputLabel sx={{ mb: 1 }}>{t("field:label.selectCurrency")}</InputLabel>
                <Autocomplete
                  value={newGroup.currency}
                  id={"currency"}
                  sx={{ width: "100%" }}
                  onChange={(e, value) => {
                    setNewGroup(
                      produce((draft) => {
                        draft!.currency = value ? CurrencyTypes[value] : null;
                      })
                    );
                  }}
                  renderInput={(params) => (
                    <OutlinedInput
                      {...params.InputProps}
                      inputProps={params.inputProps}
                      required
                      sx={{ width: "100%", mb: 1 }}
                    />
                  )}
                  options={currencies}
                  renderOption={(props, option) => (
                    <Box component="li" sx={{ "& > img": { mr: 2, flexShrink: 0 } }} {...props}>
                      {option}
                    </Box>
                  )}
                />
              </Grid>
            )}

            {labProvidersList && (
              <Grid item md={6}>
                <InputLabel sx={{ mb: 1 }}>{t("field:label.selectLabProvider")}</InputLabel>
                <Autocomplete
                  multiple
                  options={labProvidersList}
                  value={labProvidersList.filter((x) => newGroup?.labProviderIds?.includes(x.id))}
                  popupIcon={<ExpandMoreRoundedIcon />}
                  getOptionLabel={(option: any) => option.labName}
                  renderOption={(props, option) => (
                    <Box component="li" sx={{ "& > img": { mr: 2, flexShrink: 0 } }} {...props}>
                      {option.labName}
                    </Box>
                  )}
                  renderInput={(params) => (
                    <OutlinedInput
                      {...params.InputProps}
                      inputProps={params.inputProps}
                      required
                      sx={{ width: "100%", mb: 1 }}
                    />
                  )}
                  sx={{ width: "100%" }}
                  id={"labProviders"}
                  onChange={(e, value) => {
                    setNewGroup(
                      produce((draft) => {
                        draft!.labProviderIds = value.map((x) => x.id);
                      })
                    );
                  }}
                />
              </Grid>
            )}

            {entities && (
              <Grid item md={6}>
                <InputLabel sx={{ mb: 1 }}>{t("field:label.selectEntity")}</InputLabel>
                <Autocomplete
                  value={entities.find((x) => x.id === newGroup.entityId) || null}
                  id={"entity"}
                  sx={{ width: "100%" }}
                  onChange={(e, value) => {
                    setNewGroup(
                      produce((draft) => {
                        draft!.entityId = value ? value.id : null;
                      })
                    );
                  }}
                  getOptionLabel={(option: any) => option.name || null}
                  renderInput={(params) => (
                    <OutlinedInput
                      {...params.InputProps}
                      inputProps={params.inputProps}
                      required
                      sx={{ width: "100%" }}
                    />
                  )}
                  options={entities}
                  renderOption={(props, option) => (
                    <Box component="li" sx={{ "& > img": { mr: 2, flexShrink: 0 } }} {...props}>
                      {option.name}
                    </Box>
                  )}
                />
              </Grid>
            )}
          </Grid>
        </FormSection>
        <FormSection
          status={!newGroup.aclAssetAccessLevel || !newGroup.aclWorkflowAccessLevel ? "error" : "todo"}
          statusText={
            !newGroup.aclAssetAccessLevel || !newGroup.aclWorkflowAccessLevel
              ? t("form:statusText.accessLevelRequired")
              : ""
          }
          title={t("form:section.title.contextsLevelSettings")}
          sx={{ mb: 2, ml: 3 }}
        >
          <Grid container spacing={2}>
            <Grid item md={6}>
              <InputLabel required sx={{ mb: 1 }}>
                {t("field:label.workflowAccessLevel")}
              </InputLabel>
              <Autocomplete
                value={newGroup.aclWorkflowAccessLevel}
                id={"aclWorkflowAccessLevel"}
                sx={{ width: "100%" }}
                onChange={(e, value) => {
                  setNewGroup(
                    produce((draft) => {
                      draft!.aclWorkflowAccessLevel = value ? AclWorkflowAccessLevel[value] : null;
                    })
                  );
                }}
                renderInput={(params) => (
                  <OutlinedInput
                    {...params.InputProps}
                    inputProps={params.inputProps}
                    required
                    sx={{ width: "100%", mb: 1 }}
                  />
                )}
                options={Object.keys(AclWorkflowAccessLevel)}
                renderOption={(props, option) => (
                  <Box component="li" sx={{ "& > img": { mr: 2, flexShrink: 0 } }} {...props}>
                    {option}
                  </Box>
                )}
              />
            </Grid>
            <Grid item md={6}>
              <InputLabel required sx={{ mb: 1 }}>
                {t("field:label.assetAccessLevel")}
              </InputLabel>
              <Autocomplete
                value={newGroup.aclAssetAccessLevel}
                id={"aclAssetAccessLevel"}
                sx={{ width: "100%" }}
                onChange={(e, value) => {
                  setNewGroup(
                    produce((draft) => {
                      draft!.aclAssetAccessLevel = value ? AclAssetAccessLevel[value] : null;
                    })
                  );
                }}
                renderInput={(params) => (
                  <OutlinedInput
                    {...params.InputProps}
                    inputProps={params.inputProps}
                    required
                    sx={{ width: "100%", mb: 1 }}
                  />
                )}
                options={Object.keys(AclAssetAccessLevel)}
                renderOption={(props, option) => (
                  <Box component="li" sx={{ "& > img": { mr: 2, flexShrink: 0 } }} {...props}>
                    {option}
                  </Box>
                )}
              />
            </Grid>
            <Grid item md={6}>
              <InputLabel sx={{ mb: 1 }}>{t("field:label.allowedAssetsClasses")}</InputLabel>
              <Autocomplete
                multiple
                options={Object.keys(ObjectClass)}
                popupIcon={<ExpandMoreRoundedIcon />}
                value={newGroup.allowedObjectClasses || []}
                getOptionLabel={(option: any) => option}
                renderOption={(props, option) => (
                  <Box component="li" sx={{ "& > img": { mr: 2, flexShrink: 0 } }} {...props}>
                    {option}
                  </Box>
                )}
                renderInput={(params) => (
                  <OutlinedInput
                    {...params.InputProps}
                    inputProps={params.inputProps}
                    required
                    sx={{ width: "100%" }}
                  />
                )}
                sx={{ width: "100%", mb: 1 }}
                id={"allowedAssetsClasses"}
                onChange={(e, value) => {
                  setNewGroup(
                    produce((draft) => {
                      draft!.allowedObjectClasses = value ? value.map((x) => ObjectClass[x]) : null;
                    })
                  );
                }}
              />
            </Grid>
            <Grid item md={6}>
              <InputLabel>{t("field:label.allowedBusinessUnits")}</InputLabel>
              <Autocomplete
                value={newGroup.filterTitleBusinessUnits || []}
                multiple
                onChange={(e, value) => {
                  setNewGroup(
                    produce((draft) => {
                      draft!.filterTitleBusinessUnits = value;
                    })
                  );
                }}
                options={[]}
                freeSolo
                sx={{ width: "100%", mb: 1 }}
                id={"filterTitleBusinessUnits"}
                renderInput={(params) => <TextField {...params} sx={{ mt: -2 }} />}
              />
            </Grid>
            <Grid item md={6}>
              <InputLabel sx={{ mb: 1 }}>{t("field:label.filterAssets")}</InputLabel>
              <OutlinedInput
                defaultValue={newGroup.customFilters}
                id={"filterAssets"}
                sx={{ width: "100%", mb: 1 }}
                onChange={(e) =>
                  setNewGroup(
                    produce((draft) => {
                      if (!draft!.customFilters) draft!.customFilters = [];
                      if (draft!.customFilters?.find((x) => x.layer === ObjectLayer.Asset))
                        draft!.customFilters!.find((x) => x.layer === ObjectLayer.Asset)!.query = e.target.value;
                      else
                        draft!.customFilters?.push({
                          layer: ObjectLayer.Asset,
                          query: e.target.value,
                        });
                    })
                  )
                }
              />
            </Grid>
            <Grid item md={6}>
              <InputLabel sx={{ mb: 1 }}>{t("field:label.filterContents")}</InputLabel>
              <OutlinedInput
                defaultValue={newGroup.customFilters}
                id={"filterContents"}
                sx={{ width: "100%" }}
                onChange={(e) =>
                  setNewGroup(
                    produce((draft) => {
                      if (!draft!.customFilters) draft!.customFilters = [];
                      if (draft!.customFilters?.find((x) => x.layer === ObjectLayer.Content))
                        draft!.customFilters!.find((x) => x.layer === ObjectLayer.Content)!.query = e.target.value;
                      else
                        draft!.customFilters?.push({
                          layer: ObjectLayer.Content,
                          query: e.target.value,
                        });
                    })
                  )
                }
              />
            </Grid>
          </Grid>
        </FormSection>
        <FormSection title={t("form:section.title.mediaShareInfo")} sx={{ ml: 3 }} status="todo">
          <Grid container spacing={2}>
            <Grid item md={6}>
              <InputLabel sx={{ mb: 1 }}>{t("field:label.maxDays")}</InputLabel>
              <OutlinedInput
                defaultValue={newGroup.maxShareViewDays}
                id={"maxShareViewDays"}
                sx={{ width: "100%", mb: 1 }}
                type={"number"}
                onChange={(e) => {
                  setNewGroup(
                    produce((draft) => {
                      draft!.maxShareViewDays = +e.target.value;
                    })
                  );
                }}
              />
            </Grid>
            <Grid item md={6}>
              <InputLabel sx={{ mb: 1 }}>{t("field:label.maxViews")}</InputLabel>
              <OutlinedInput
                defaultValue={newGroup.maxShareViews}
                id={"maxShareViews"}
                type={"number"}
                sx={{ width: "100%", mb: 1 }}
                onChange={(e) => {
                  setNewGroup(
                    produce((draft) => {
                      draft!.maxShareViews = +e.target.value;
                    })
                  );
                }}
              />
            </Grid>
            <Grid item md={6}>
              <InputLabel sx={{ mb: 1 }}>{t("field:label.maxIPs")}</InputLabel>
              <OutlinedInput
                defaultValue={newGroup.maxShareViewIPs}
                id={"maxShareViewIPs"}
                type={"number"}
                sx={{ width: "100%", mb: 1 }}
                onChange={(e) => {
                  setNewGroup(
                    produce((draft) => {
                      draft!.maxShareViewIPs = +e.target.value;
                    })
                  );
                }}
              />
            </Grid>
            <Grid item md={6}>
              <InputLabel sx={{ mb: 1 }}>{t("field:label.maxNumberOfClips")}</InputLabel>
              <OutlinedInput
                defaultValue={newGroup.maxShareViewClipsNumber}
                id={"maxShareViewClipsNumber"}
                sx={{ width: "100%", mb: 1 }}
                type={"number"}
                onChange={(e) =>
                  setNewGroup(
                    produce((draft) => {
                      draft!.maxShareViewClipsNumber = +e.target.value;
                    })
                  )
                }
              />
            </Grid>
            <Grid item md={6}>
              <InputLabel sx={{ mb: 1 }}>{t("field:label.maxClipDuration")}</InputLabel>
              <OutlinedInput
                defaultValue={timeSpanToMinutes(newGroup.maxShareViewClipsDuration)}
                id={"maxShareViewClipsDuration"}
                sx={{ width: "100%", mb: 1 }}
                type={"number"}
                onChange={(e) => {
                  setNewGroup(
                    produce((draft) => {
                      draft!.maxShareViewClipsDuration = minutesToTimeSpan(e.target.value);
                    })
                  );
                }}
              />
            </Grid>
            <Grid item md={6}>
              <InputLabel sx={{ mb: 1 }}>{t("field:label.defaultDays")}</InputLabel>
              <OutlinedInput
                defaultValue={newGroup.defaultShareViewDays}
                id={"defaultShareViewDays"}
                sx={{ width: "100%", mb: 1 }}
                type={"number"}
                onChange={(e) => {
                  setNewGroup(
                    produce((draft) => {
                      draft!.defaultShareViewDays = +e.target.value;
                    })
                  );
                }}
              />
            </Grid>
            <Grid item md={6}>
              <InputLabel sx={{ mb: 1 }}>{t("field:label.defaultViews")}</InputLabel>
              <OutlinedInput
                defaultValue={newGroup.defaultShareViews}
                id={"defaultShareViews"}
                sx={{ width: "100%", mb: 1 }}
                type={"number"}
                onChange={(e) => {
                  setNewGroup(
                    produce((draft) => {
                      draft!.defaultShareViews = +e.target.value;
                    })
                  );
                }}
              />
            </Grid>
            <Grid item md={6}>
              <InputLabel sx={{ mb: 1 }}>{t("field:label.defaultIPs")}</InputLabel>
              <OutlinedInput
                defaultValue={newGroup.defaultShareViewIPs}
                id={"defaultShareViewIPs"}
                sx={{ width: "100%", mb: 1 }}
                type={"number"}
                onChange={(e) =>
                  setNewGroup(
                    produce((draft) => {
                      draft!.defaultShareViewIPs = +e.target.value;
                    })
                  )
                }
              />
            </Grid>
            <Grid item md={6}>
              <InputLabel sx={{ mb: 1 }}>{t("field:label.defaultNumberOfClips")}</InputLabel>
              <OutlinedInput
                defaultValue={newGroup.defaultShareViewClipsNumber}
                id={"defaultShareViewClipsNumber"}
                sx={{ width: "100%", mb: 1 }}
                type={"number"}
                onChange={(e) => {
                  setNewGroup(
                    produce((draft) => {
                      draft!.defaultShareViewClipsNumber = +e.target.value;
                    })
                  );
                }}
              />
            </Grid>
            <Grid item md={6}>
              <InputLabel sx={{ mb: 1 }}>{t("field:label.defaultClipDuration")}</InputLabel>
              <OutlinedInput
                defaultValue={timeSpanToMinutes(newGroup.defaultShareViewClipsDuration)}
                id={"defaultShareViewClipsDuration"}
                sx={{ width: "100%", mb: 1 }}
                onChange={(e) =>
                  setNewGroup(
                    produce((draft) => {
                      draft!.defaultShareViewClipsDuration = minutesToTimeSpan(e.target.value);
                    })
                  )
                }
              />
            </Grid>
            <Grid item md={6}>
              <InputLabel sx={{ mb: 1 }}>{t("field:label.defaultClipName")}</InputLabel>
              <OutlinedInput
                defaultValue={newGroup.defaultShareName}
                id={"defaultShareName"}
                sx={{ width: "100%", mb: 1 }}
                type={"text"}
                onChange={(e) =>
                  setNewGroup(
                    produce((draft) => {
                      draft!.defaultShareName = e.target.value;
                    })
                  )
                }
              />
            </Grid>
          </Grid>
        </FormSection>
      </Grid>
      <Divider sx={{ mt: 3, mb: 3 }} />
      <Stack direction="row" alignItems="center" spacing={2} justifyContent="space-between" mb={2}>
        <Typography variant="h5">{t("modal:body.groupPermissions")}</Typography>
        <Button
          size="small"
          onClick={onClickCopyPermissionsGroup}
          endIcon={
            <ExpandMoreRoundedIcon
              sx={{ transform: permissionAnchorGroup ? "rotate(-180deg)" : "none", transition: "transform .3s" }}
            />
          }
        >
          {t("button:copyPermissionsFromGroup")}
        </Button>
        <Button
          size="small"
          onClick={onClickCopyPermissionsRole}
          endIcon={
            <ExpandMoreRoundedIcon
              sx={{ transform: permissionAnchorRole ? "rotate(-180deg)" : "none", transition: "transform .3s" }}
            />
          }
        >
          {t("button:copyPermissionsFromRole")}
        </Button>
        <CopyPermissionPopperRole
          anchorEl={permissionAnchorRole}
          allRoles={groupRoles}
          onClickConfirm={onClickCopyPermissionsRoleValidation}
          onClose={() => setPermissionAnchorRole(undefined)}
        />
        <CopyPermissionPopperGroup
          anchorEl={permissionAnchorGroup}
          onClickConfirm={onClickCopyPermissionsGroupValidation}
          onClose={() => setPermissionAnchorGroup(undefined)}
        />
      </Stack>
      {/*<Alert severity="info">Permissions have been imported from "Customer" permissions</Alert>*/}
      <OutlinedInput
        label={t("common:filter")}
        sx={{ width: "100%", mt: 2 }}
        placeholder={t("common:filter")}
        onChange={(e) => setFilterValue(e.currentTarget.value)}
        endAdornment={
          <InputAdornment position="end">
            <SearchIcon />
          </InputAdornment>
        }
      />
      <PermissionAccordionList
        searchValue={filterValue}
        acls={newGroup.acls ?? []}
        setAcls={(v) =>
          setNewGroup(
            produce((draft) => {
              draft.acls = v as AclTypes[];
            })
          )
        }
      />
    </Modal>
  );
});

export default ModalAddGroup;
